import AlertTypes from "enums/alertTypes";
import { type BasePerformanceRequestFilterParams } from "features/Library/Common/models";
import { convertInteractionDisplayName, sendExportRequest } from "features/Library/Common/utils/performanceUtils";
import { type UserAssetTypes } from "features/People/Users/Performance/types";
import { peopleReportingService } from "features/Reporting/services/peopleReportingService";

type DetailsFilterParams = BasePerformanceRequestFilterParams & { type: string };
type DetailsFilterInteractionParams = DetailsFilterParams & { type: UserAssetTypes };

export const getUserActivityExport = (params: BasePerformanceRequestFilterParams) => {
  let abortController = new AbortController();
  return sendExportRequest(
    (abortController) => peopleReportingService.activity.getUserActivityExport({ ...params }, abortController.signal),
    "User Activity",
    abortController,
  );
};

export const getPeopleActivityExport = (
  params: BasePerformanceRequestFilterParams,
  hasOverMillionInteractions: boolean,
  accountId: number,
) => {
  let abortController = new AbortController();

  const customSuccessNotification = hasOverMillionInteractions
    ? {
        title: "Export Complete!",
        message: (
          <>
            Your file download has started.{" "}
            <i>
              <strong>Please note: </strong>This file contains over 1 million rows, which exceeds Excel's limit per
              sheet. Rows beyond that will not be included.
            </i>
          </>
        ),
        type: AlertTypes.success,
      }
    : undefined;

  return sendExportRequest(
    (abortController) =>
      peopleReportingService.activity.getActivityExport({ ...params }, abortController.signal, accountId),
    "People Activity",
    abortController,
    customSuccessNotification,
  );
};

export const getPeopleOutreachExport = (params: DetailsFilterParams) => {
  let abortController = new AbortController();
  return sendExportRequest(
    (abortController) => peopleReportingService.activity.getPeopleOutreachExport(params),
    params.type,
    abortController,
  );
};

export const getPeopleInteractionExport = (params: DetailsFilterInteractionParams) => {
  let abortController = new AbortController();
  return sendExportRequest(
    (abortController) => peopleReportingService.activity.getPeopleInteractionExport(params),
    convertInteractionDisplayName(params.type),
    abortController,
  );
};

export const getPeopleAcquisitionExport = (params: BasePerformanceRequestFilterParams) => {
  let abortController = new AbortController();
  return sendExportRequest(
    (abortController) => peopleReportingService.acquisition.getAcquisitionExport(params),
    "Medium Interactions",
    abortController,
  );
};

export const getPeopleAcquisitionDrilldownExport = (
  params: BasePerformanceRequestFilterParams,
  campaign: string,
  medium: string,
  source: string,
) => {
  let abortController = new AbortController();
  return sendExportRequest(
    (abortController) =>
      peopleReportingService.acquisition.getAcquisitionDrilldownExport({
        ...params,
        campaign,
        medium,
        source,
      }),
    "Users",
    abortController,
  );
};
